import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Typography, Upload, List, Progress, message, Tag, Space, Modal, Tabs } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;
const IOS_STAGES = ['상품 조회', '상품 등록', '현지화', '티어 획득', '가격 설정'];
const AOS_STAGES = ['상품 등록'];

const UploadProgressStream = () => {
    const [files, setFiles] = useState({});
    const [uploading, setUploading] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [modal, setModal] = useState({ open: false, message: '' });
    const [activeTab, setActiveTab] = useState('ios');

    const beforeUpload = (file, name) => {
        setFiles(prev => ({ ...prev, [name]: file }));
        return false;
    };

    const abortControllerRef = useRef(null);

    const handleUpload = async (platform) => {
        if (!files.product || !files.productLocale || !files.price) {
            return message.error('모든 CSV 파일을 업로드해주세요.');
        }

        setUploading(true);
        setTasks([]);
        abortControllerRef.current = new AbortController();

        try {
            const formData = new FormData();
            formData.append('product', files.product);
            formData.append('productLocale', files.productLocale);
            formData.append('price', files.price);
            formData.append('platform', platform);

            const baseURL = process.env.REACT_APP_URL || '';

            const response = await fetch(`${baseURL}in-app/upload-csv`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let lines = buffer.split('\n\n');
                buffer = lines.pop();

                for (let line of lines) {
                    if (!line.startsWith('data:')) continue;
                    const data = line.replace('data: ', '');
                    if (data === '[DONE]') {
                        setUploading(false);
                        return;
                    }

                    try {
                        const parsed = JSON.parse(data);
                        setTasks(prev => {
                            const copy = [...prev];
                            const index = copy.findIndex(t => t.name === parsed.name);
                            if (index !== -1) {
                                copy[index] = parsed;
                            } else {
                                copy.push(parsed);
                            }
                            return copy;
                        });
                    } catch (err) {
                        console.error('파싱 오류:', err);
                    }
                }
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                message.error('업로드 중 오류가 발생했습니다.');
                console.error('Upload error:', err);
            }
        } finally {
            setUploading(false);
        }
    };

    const getStageColor = (item, idx) => {
        if (item.errorStage !== undefined) {
            if (idx < item.errorStage) return '#52c41a';
            if (idx === item.errorStage) return '#ff4d4f';
            return '#999';
        }
        if (item.status?.[idx]) return '#52c41a';
        if (item.error) return '#ff4d4f';
        return '#999';
    };

    const handleClickError = (item) => {
        if (item.errorMessage) {
            setModal({ open: true, message: item.errorMessage });
        }
    };

    const renderFileUpload = (fileName, tagColor) => (
        <Space>
            <Upload beforeUpload={(file) => beforeUpload(file, fileName)} showUploadList={false} accept=".csv">
                <Button icon={<UploadOutlined />}>{fileName}.csv</Button>
            </Upload>
            {files[fileName] && <Tag color={tagColor}>{files[fileName].name}</Tag>}
        </Space>
    );

    const renderProgressList = (platform) => {
        if (tasks.length === 0) return null;
        
        const stages = platform === 'ios' ? IOS_STAGES : AOS_STAGES;
        
        return (
            <List
                header={<div>진행 현황</div>}
                dataSource={tasks}
                renderItem={item => {
                    const completed = item.status?.filter(Boolean).length || 0;
                    const stageCount = stages.length;
                    const percent = Math.round((completed / stageCount) * 100);
                    return (
                        <List.Item>
                            <div style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                                    <strong>{item.name}</strong>
                                    <Progress percent={percent} steps={stageCount} strokeColor={item.error ? '#ff4d4f' : '#52c41a'} style={{ flex: 1 }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                                    {stages.map((stage, idx) => (
                                        <span
                                            key={idx}
                                            style={{
                                                color: getStageColor(item, idx),
                                                fontWeight: idx === item.errorStage ? 'bold' : 'normal',
                                                cursor: idx === item.errorStage ? 'pointer' : 'default',
                                                textDecoration: idx === item.errorStage ? 'underline' : 'none'
                                            }}
                                            onClick={() => idx === item.errorStage && handleClickError(item)}
                                        >
                                            {stage}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </List.Item>
                    );
                }}
                style={{ marginTop: 30 }}
            />
        );
    };

    const renderTabContent = (platform, description, tagColor) => (
        <>
            <Paragraph>{description}</Paragraph>
            <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 20 }}>
                {renderFileUpload('product', tagColor)}
                {renderFileUpload('productLocale', tagColor)}
                {renderFileUpload('price', tagColor)}
            </Space>
            <Button 
                type="primary" 
                onClick={() => handleUpload(platform)} 
                disabled={uploading} 
                loading={uploading} 
                style={{ width: '100%' }}
            >
                {platform.toUpperCase()} 업로드 시작
            </Button>
            {renderProgressList(platform)}
        </>
    );

    const iosTabContent = renderTabContent(
        'ios', 
        '3개의 CSV 파일을 업로드하고 등록 진행 상황을 실시간으로 확인할 수 있습니다.', 
        'blue'
    );

    const aosTabContent = renderTabContent(
        'aos', 
        '3개의 CSV 파일을 업로드하고 Google Play Console에 인앱 상품을 등록합니다.', 
        'green'
    );

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <Card style={{ maxWidth: 800, margin: '40px auto' }}>
            <Title level={4}>CSV 업로드</Title>
            <Tabs 
                defaultActiveKey="ios"
                onChange={(key) => {
                    setActiveTab(key);
                    setTasks([]);
                }}
                items={[
                    { key: 'ios', label: <strong style={{ fontSize: 16 }}>📱 iOS</strong>, children: iosTabContent },
                    { key: 'aos', label: <strong style={{ fontSize: 16 }}>🤖 AOS</strong>, children: aosTabContent },
                ]}
            />

            <Modal
                open={modal.open}
                onCancel={() => setModal({ open: false, message: '' })}
                footer={null}
                title="오류 상세"
            >
                <p>{modal.message}</p>
            </Modal>
        </Card>
    );
};

export default UploadProgressStream;